export interface PerformanceMetric {
  id: string
  timestamp: number
  type: 'chat' | 'analysis' | 'report' | 'summary' | 'translation'
  duration: number
  tokens?: number
  model: string
  mode: 'local' | 'cloud'
  cached: boolean
  success: boolean
  error?: string
  aiId?: string
  userQuestion?: string
  aiResponse?: string
  responseQuality?: 'excellent' | 'good' | 'fair' | 'poor'
  relevanceScore?: number
  helpfulnessScore?: number
  userFeedback?: 'like' | 'dislike' | null
}

export interface PerformanceStats {
  totalRequests: number
  successfulRequests: number
  failedRequests: number
  averageResponseTime: number
  cacheHitRate: number
  cloudModeRate: number
  localModeRate: number
  tokenUsage: {
    total: number
    average: number
  }
  errorRate: number
  topErrors: Array<{ message: string; count: number }>
}

export interface SystemHealth {
  status: 'healthy' | 'warning' | 'error'
  uptime: number
  lastRequest: Date | null
  activeModels: string[]
  systemLoad: number
  warnings: string[]
  errors: string[]
}

export class AIPerformanceMonitor {
  private metrics: PerformanceMetric[] = []
  private maxMetrics: number = 1000
  private sessionStartTime: number
  private lastRequestTimestamp: number | null = null
  private activeModels: Set<string> = new Set()
  private warnings: string[] = []
  private errors: string[] = []
  private requestCounter: number = 0

  constructor() {
    this.sessionStartTime = Date.now()
  }

  startTimer(): () => number {
    const startTime = Date.now()
    return () => Date.now() - startTime
  }

  recordMetric(metric: Omit<PerformanceMetric, 'id' | 'timestamp'>): string {
    const id = `metric-${Date.now()}-${++this.requestCounter}`
    const timestamp = Date.now()
    
    const fullMetric: PerformanceMetric = {
      ...metric,
      id,
      timestamp
    }

    this.metrics.push(fullMetric)
    this.lastRequestTimestamp = timestamp
    this.activeModels.add(metric.model)

    if (!metric.success && metric.error) {
      this.errors.push(`[${new Date().toISOString()}] ${metric.error}`)
      if (this.errors.length > 50) {
        this.errors.shift()
      }
    }

    if (this.metrics.length > this.maxMetrics) {
      this.metrics.shift()
    }

    return id
  }

  getMetrics(options: {
    limit?: number
    type?: string
    mode?: string
    startDate?: number
    endDate?: number
  } = {}): PerformanceMetric[] {
    let result = [...this.metrics]

    if (options.type) {
      result = result.filter(m => m.type === options.type)
    }

    if (options.mode) {
      result = result.filter(m => m.mode === options.mode)
    }

    if (options.startDate) {
      result = result.filter(m => m.timestamp >= options.startDate)
    }

    if (options.endDate) {
      result = result.filter(m => m.timestamp <= options.endDate)
    }

    if (options.limit) {
      result = result.slice(-options.limit)
    }

    return result
  }

  getStats(options: {
    startDate?: number
    endDate?: number
  } = {}): PerformanceStats {
    const metrics = this.getMetrics(options)
    
    const totalRequests = metrics.length
    const successfulRequests = metrics.filter(m => m.success).length
    const failedRequests = totalRequests - successfulRequests
    const averageResponseTime = totalRequests > 0
      ? metrics.reduce((sum, m) => sum + m.duration, 0) / totalRequests
      : 0
    const cacheHits = metrics.filter(m => m.cached).length
    const cacheHitRate = totalRequests > 0 ? cacheHits / totalRequests : 0
    const cloudModeRequests = metrics.filter(m => m.mode === 'cloud').length
    const cloudModeRate = totalRequests > 0 ? cloudModeRequests / totalRequests : 0
    const localModeRate = 1 - cloudModeRate
    const totalTokens = metrics.filter(m => m.tokens).reduce((sum, m) => sum + (m.tokens || 0), 0)
    const averageTokens = metrics.filter(m => m.tokens).length > 0
      ? totalTokens / metrics.filter(m => m.tokens).length
      : 0
    const errorRate = totalRequests > 0 ? failedRequests / totalRequests : 0

    const errorCounts = new Map<string, number>()
    metrics.filter(m => !m.success && m.error).forEach(m => {
      if (m.error) {
        const key = m.error.substring(0, 100)
        errorCounts.set(key, (errorCounts.get(key) || 0) + 1)
      }
    })

    const topErrors = Array.from(errorCounts.entries())
      .map(([message, count]) => ({ message, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)

    const responseQualityMetrics = metrics.filter(m => m.responseQuality)
    const excellentCount = responseQualityMetrics.filter(m => m.responseQuality === 'excellent').length
    const goodCount = responseQualityMetrics.filter(m => m.responseQuality === 'good').length
    const fairCount = responseQualityMetrics.filter(m => m.responseQuality === 'fair').length
    const poorCount = responseQualityMetrics.filter(m => m.responseQuality === 'poor').length
    
    const relevanceScores = metrics.filter(m => m.relevanceScore).map(m => m.relevanceScore!)
    const averageRelevance = relevanceScores.length > 0
      ? relevanceScores.reduce((sum, s) => sum + s, 0) / relevanceScores.length
      : 0

    const helpfulnessScores = metrics.filter(m => m.helpfulnessScore).map(m => m.helpfulnessScore!)
    const averageHelpfulness = helpfulnessScores.length > 0
      ? helpfulnessScores.reduce((sum, s) => sum + s, 0) / helpfulnessScores.length
      : 0

    const feedbackMetrics = metrics.filter(m => m.userFeedback)
    const likes = feedbackMetrics.filter(m => m.userFeedback === 'like').length
    const dislikes = feedbackMetrics.filter(m => m.userFeedback === 'dislike').length
    const likeRate = feedbackMetrics.length > 0 ? likes / feedbackMetrics.length : 0

    const aiDistribution: Record<string, number> = {}
    metrics.forEach(m => {
      const aiId = m.aiId || 'unknown'
      aiDistribution[aiId] = (aiDistribution[aiId] || 0) + 1
    })

    const requestTypes: Record<string, number> = {}
    metrics.forEach(m => {
      requestTypes[m.type] = (requestTypes[m.type] || 0) + 1
    })

    return {
      totalRequests,
      successfulRequests,
      failedRequests,
      averageResponseTime,
      cacheHitRate,
      cloudModeRate,
      localModeRate,
      tokenUsage: {
        total: totalTokens,
        average: averageTokens
      },
      errorRate,
      topErrors,
      responseQuality: {
        excellent: excellentCount,
        good: goodCount,
        fair: fairCount,
        poor: poorCount,
        averageRelevance,
        averageHelpfulness
      },
      userFeedback: {
        likes,
        dislikes,
        likeRate
      },
      aiDistribution,
      requestTypes
    }
  }

  getSystemHealth(): SystemHealth {
    const now = Date.now()
    const uptime = now - this.sessionStartTime
    const lastRequest = this.lastRequestTimestamp ? new Date(this.lastRequestTimestamp) : null
    
    const recentMetrics = this.getMetrics({ limit: 100 })
    const recentErrors = recentMetrics.filter(m => !m.success).length
    const recentErrorRate = recentMetrics.length > 0 ? recentErrors / recentMetrics.length : 0
    
    let status: 'healthy' | 'warning' | 'error' = 'healthy'
    const warnings: string[] = []
    const errors: string[] = [...this.errors.slice(-20)]

    if (recentErrorRate > 0.2) {
      status = 'error'
      warnings.push(`近期错误率过高: ${(recentErrorRate * 100).toFixed(1)}%`)
    } else if (recentErrorRate > 0.1) {
      status = 'warning'
      warnings.push(`近期错误率偏高: ${(recentErrorRate * 100).toFixed(1)}%`)
    }

    const stats = this.getStats()
    if (stats.averageResponseTime > 5000) {
      status = 'warning'
      warnings.push(`平均响应时间过长: ${(stats.averageResponseTime / 1000).toFixed(1)}秒`)
    }

    if (this.lastRequestTimestamp && now - this.lastRequestTimestamp > 3600000) {
      warnings.push('超过1小时无活动')
    }

    warnings.push(...this.warnings.slice(-10))

    return {
      status,
      uptime,
      lastRequest,
      activeModels: Array.from(this.activeModels),
      systemLoad: Math.min(1, recentMetrics.length / 100),
      warnings,
      errors
    }
  }

  getRealtimeMetrics() {
    const now = Date.now()
    const oneMinuteAgo = now - 60000
    const fiveMinutesAgo = now - 300000
    const fifteenMinutesAgo = now - 900000

    const stats1m = this.getStats({ startDate: oneMinuteAgo })
    const stats5m = this.getStats({ startDate: fiveMinutesAgo })
    const stats15m = this.getStats({ startDate: fifteenMinutesAgo })

    return {
      oneMinute: stats1m,
      fiveMinutes: stats5m,
      fifteenMinutes: stats15m,
      currentHealth: this.getSystemHealth()
    }
  }

  addWarning(message: string): void {
    const timestampedMessage = `[${new Date().toISOString()}] ${message}`
    this.warnings.push(timestampedMessage)
    if (this.warnings.length > 50) {
      this.warnings.shift()
    }
  }

  clearWarnings(): void {
    this.warnings = []
  }

  clearErrors(): void {
    this.errors = []
  }

  clearAll(): void {
    this.metrics = []
    this.warnings = []
    this.errors = []
    this.requestCounter = 0
    this.activeModels.clear()
  }

  exportReport(): string {
    const stats = this.getStats()
    const health = this.getSystemHealth()
    
    const report = {
      generatedAt: new Date().toISOString(),
      sessionUptime: health.uptime,
      stats,
      health,
      recentMetrics: this.getMetrics({ limit: 50 })
    }

    return JSON.stringify(report, null, 2)
  }

  getDashboardData() {
    const realtime = this.getRealtimeMetrics()
    const health = this.getSystemHealth()
    const overallStats = this.getStats()
    
    return {
      realtime,
      health,
      overall: overallStats,
      recentActivity: this.getMetrics({ limit: 20 }).reverse(),
      trendData: this.generateTrendData()
    }
  }

  private generateTrendData() {
    const now = Date.now()
    const data = []
    
    for (let i = 23; i >= 0; i--) {
      const hourStart = now - i * 3600000
      const hourEnd = hourStart + 3600000
      const stats = this.getStats({ startDate: hourStart, endDate: hourEnd })
      
      data.push({
        hour: new Date(hourStart).getHours(),
        requests: stats.totalRequests,
        errors: stats.failedRequests,
        avgDuration: stats.averageResponseTime,
        avgRelevance: stats.responseQuality.averageRelevance,
        likeRate: stats.userFeedback.likeRate
      })
    }
    
    return data
  }

  evaluateResponseQuality(userQuestion: string, aiResponse: string): {
    quality: 'excellent' | 'good' | 'fair' | 'poor'
    relevanceScore: number
    helpfulnessScore: number
  } {
    let relevanceScore = 0
    let helpfulnessScore = 0

    const questionLength = userQuestion.length
    const responseLength = aiResponse.length

    if (responseLength < 10) {
      relevanceScore = 0.2
      helpfulnessScore = 0.1
    } else if (responseLength < 50) {
      relevanceScore = 0.4
      helpfulnessScore = 0.3
    } else {
      relevanceScore = this.calculateRelevance(userQuestion, aiResponse)
      helpfulnessScore = this.calculateHelpfulness(aiResponse)
    }

    let quality: 'excellent' | 'good' | 'fair' | 'poor' = 'fair'
    const avgScore = (relevanceScore + helpfulnessScore) / 2
    
    if (avgScore >= 0.85) quality = 'excellent'
    else if (avgScore >= 0.7) quality = 'good'
    else if (avgScore >= 0.5) quality = 'fair'
    else quality = 'poor'

    return { quality, relevanceScore, helpfulnessScore }
  }

  private calculateRelevance(question: string, response: string): number {
    const questionKeywords = this.extractKeywords(question)
    const responseKeywords = this.extractKeywords(response)
    
    if (questionKeywords.length === 0) return 0.5

    let matchCount = 0
    questionKeywords.forEach(keyword => {
      if (responseKeywords.includes(keyword)) {
        matchCount++
      }
    })

    return Math.min(1, matchCount / questionKeywords.length)
  }

  private calculateHelpfulness(response: string): number {
    let score = 0.5

    if (response.includes('步骤') || response.includes('方法') || response.includes('建议')) {
      score += 0.15
    }
    if (response.includes('可以') || response.includes('能够') || response.includes('提供')) {
      score += 0.1
    }
    if (response.includes('！') || response.includes('。') || response.includes('？')) {
      score += 0.05
    }
    if (response.length > 200) {
      score += 0.1
    }

    return Math.min(1, score)
  }

  private extractKeywords(text: string): string[] {
    const stopWords = ['的', '是', '在', '有', '和', '了', '我', '你', '他', '她', '它', '这', '那', '能', '会', '可以', '不', '要', '都', '就', '而', '也', '很', '还', '又', '再', '更', '比', '但', '如果', '因为', '所以', '虽然', '但是', '还是', '或者', '就是', '还是']
    const words = text.toLowerCase().match(/[\u4e00-\u9fa5a-zA-Z]+/g) || []
    return words.filter(word => !stopWords.includes(word) && word.length > 1)
  }

  recordAIInteraction(aiId: string, userQuestion: string, aiResponse: string, duration: number, success: boolean = true, error?: string) {
    const qualityResult = this.evaluateResponseQuality(userQuestion, aiResponse)
    
    this.recordMetric({
      type: 'chat',
      duration,
      model: 'frontend-ai',
      mode: 'local',
      cached: false,
      success,
      error,
      aiId,
      userQuestion,
      aiResponse,
      responseQuality: qualityResult.quality,
      relevanceScore: qualityResult.relevanceScore,
      helpfulnessScore: qualityResult.helpfulnessScore,
      userFeedback: null
    })
  }

  recordUserFeedback(metricId: string, feedback: 'like' | 'dislike') {
    const metric = this.metrics.find(m => m.id === metricId)
    if (metric) {
      metric.userFeedback = feedback
    }
  }

  getAIQualityReport(aiId?: string): {
    aiId: string
    totalInteractions: number
    averageRelevance: number
    averageHelpfulness: number
    qualityDistribution: { excellent: number; good: number; fair: number; poor: number }
    feedbackRate: number
    likeRate: number
    topQuestions: Array<{ question: string; count: number }>
    commonIssues: string[]
  } {
    let metrics = aiId 
      ? this.metrics.filter(m => m.aiId === aiId)
      : this.metrics.filter(m => m.aiId)

    const totalInteractions = metrics.length
    
    const relevanceScores = metrics.filter(m => m.relevanceScore).map(m => m.relevanceScore!)
    const averageRelevance = relevanceScores.length > 0
      ? relevanceScores.reduce((sum, s) => sum + s, 0) / relevanceScores.length
      : 0

    const helpfulnessScores = metrics.filter(m => m.helpfulnessScore).map(m => m.helpfulnessScore!)
    const averageHelpfulness = helpfulnessScores.length > 0
      ? helpfulnessScores.reduce((sum, s) => sum + s, 0) / helpfulnessScores.length
      : 0

    const qualityDistribution = {
      excellent: metrics.filter(m => m.responseQuality === 'excellent').length,
      good: metrics.filter(m => m.responseQuality === 'good').length,
      fair: metrics.filter(m => m.responseQuality === 'fair').length,
      poor: metrics.filter(m => m.responseQuality === 'poor').length
    }

    const feedbackMetrics = metrics.filter(m => m.userFeedback)
    const feedbackRate = totalInteractions > 0 ? feedbackMetrics.length / totalInteractions : 0
    const likes = feedbackMetrics.filter(m => m.userFeedback === 'like').length
    const likeRate = feedbackMetrics.length > 0 ? likes / feedbackMetrics.length : 0

    const questionCounts = new Map<string, number>()
    metrics.forEach(m => {
      if (m.userQuestion) {
        const key = m.userQuestion.substring(0, 50)
        questionCounts.set(key, (questionCounts.get(key) || 0) + 1)
      }
    })

    const topQuestions = Array.from(questionCounts.entries())
      .map(([question, count]) => ({ question, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)

    const commonIssues: string[] = []
    const poorResponses = metrics.filter(m => m.responseQuality === 'poor')
    if (poorResponses.length > totalInteractions * 0.2) {
      commonIssues.push('响应质量普遍较低')
    }
    if (averageRelevance < 0.5) {
      commonIssues.push('回答相关性不足')
    }
    if (averageHelpfulness < 0.5) {
      commonIssues.push('回答帮助性不足')
    }

    return {
      aiId: aiId || 'all',
      totalInteractions,
      averageRelevance,
      averageHelpfulness,
      qualityDistribution,
      feedbackRate,
      likeRate,
      topQuestions,
      commonIssues
    }
  }

  getConversationAnalysis(limit: number = 50): Array<{
    id: string
    timestamp: Date
    aiId: string
    question: string
    response: string
    quality: string
    relevance: number
    helpfulness: number
    feedback?: string
  }> {
    return this.metrics
      .filter(m => m.aiId && m.userQuestion)
      .slice(-limit)
      .reverse()
      .map(m => ({
        id: m.id,
        timestamp: new Date(m.timestamp),
        aiId: m.aiId!,
        question: m.userQuestion!,
        response: m.aiResponse || '',
        quality: m.responseQuality || 'unknown',
        relevance: m.relevanceScore || 0,
        helpfulness: m.helpfulnessScore || 0,
        feedback: m.userFeedback
      }))
  }

  generateImprovementSuggestions(): Array<{
    priority: 'high' | 'medium' | 'low'
    category: string
    issue: string
    suggestion: string
    impact: string
  }> {
    const suggestions: Array<{
      priority: 'high' | 'medium' | 'low'
      category: string
      issue: string
      suggestion: string
      impact: string
    }> = []

    const stats = this.getStats()
    const qualityReport = this.getAIQualityReport()

    if (stats.errorRate > 0.1) {
      suggestions.push({
        priority: 'high',
        category: '系统稳定性',
        issue: `错误率过高 (${(stats.errorRate * 100).toFixed(1)}%)`,
        suggestion: '检查服务端API健康状态，排查高频错误原因',
        impact: '降低错误率可提升用户体验和信任度'
      })
    }

    if (stats.averageResponseTime > 3000) {
      suggestions.push({
        priority: 'high',
        category: '响应性能',
        issue: `平均响应时间过长 (${(stats.averageResponseTime / 1000).toFixed(1)}秒)`,
        suggestion: '优化AI模型推理速度，考虑引入缓存机制',
        impact: '提升响应速度可显著改善用户体验'
      })
    }

    if (qualityReport.averageRelevance < 0.6) {
      suggestions.push({
        priority: 'high',
        category: '回答质量',
        issue: `回答相关性不足 (${(qualityReport.averageRelevance * 100).toFixed(1)}%)`,
        suggestion: '扩展知识库，优化意图识别模型',
        impact: '提升回答相关性可提高用户满意度'
      })
    }

    if (qualityReport.averageHelpfulness < 0.6) {
      suggestions.push({
        priority: 'medium',
        category: '回答质量',
        issue: `回答帮助性不足 (${(qualityReport.averageHelpfulness * 100).toFixed(1)}%)`,
        suggestion: '优化回答模板，增加步骤指引和实用信息',
        impact: '更有帮助的回答可提升用户留存'
      })
    }

    if (qualityReport.qualityDistribution.poor > qualityReport.totalInteractions * 0.15) {
      suggestions.push({
        priority: 'medium',
        category: '回答质量',
        issue: '低质量回答占比过高',
        suggestion: '审查低质量回答案例，优化AI训练数据',
        impact: '减少低质量回答可提升整体服务品质'
      })
    }

    if (qualityReport.feedbackRate < 0.1) {
      suggestions.push({
        priority: 'low',
        category: '用户反馈',
        issue: `用户反馈率过低 (${(qualityReport.feedbackRate * 100).toFixed(1)}%)`,
        suggestion: '优化反馈入口设计，增加反馈激励机制',
        impact: '更多反馈可帮助持续改进AI性能'
      })
    }

    if (qualityReport.likeRate < 0.7) {
      suggestions.push({
        priority: 'medium',
        category: '用户满意度',
        issue: `用户满意度较低 (${(qualityReport.likeRate * 100).toFixed(1)}%)`,
        suggestion: '分析负面反馈原因，针对性改进',
        impact: '提高满意度可增加用户粘性'
      })
    }

    return suggestions.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 }
      return priorityOrder[a.priority] - priorityOrder[b.priority]
    })
  }

  getPerformanceInsights(): {
    summary: string
    keyMetrics: Array<{ name: string; value: string; status: 'good' | 'warning' | 'critical' }>
    trends: Array<{ label: string; requests: number; errors: number; avgDuration: number }>
    topAI: Array<{ aiId: string; interactions: number; avgQuality: number }>
    commonQuestions: Array<{ question: string; count: number }>
    improvementSuggestions: Array<{
      priority: 'high' | 'medium' | 'low'
      category: string
      issue: string
      suggestion: string
    }>
  } {
    const stats = this.getStats()
    const qualityReport = this.getAIQualityReport()
    const trendData = this.generateTrendData()

    const keyMetrics = [
      {
        name: '总请求数',
        value: stats.totalRequests.toString(),
        status: stats.totalRequests > 0 ? 'good' : 'warning' as const
      },
      {
        name: '成功率',
        value: `${((stats.successfulRequests / stats.totalRequests) * 100 || 0).toFixed(1)}%`,
        status: stats.errorRate < 0.1 ? 'good' : stats.errorRate < 0.2 ? 'warning' : 'critical' as const
      },
      {
        name: '平均响应时间',
        value: `${(stats.averageResponseTime / 1000).toFixed(2)}s`,
        status: stats.averageResponseTime < 2000 ? 'good' : stats.averageResponseTime < 4000 ? 'warning' : 'critical' as const
      },
      {
        name: '平均相关性',
        value: `${(qualityReport.averageRelevance * 100).toFixed(1)}%`,
        status: qualityReport.averageRelevance >= 0.7 ? 'good' : qualityReport.averageRelevance >= 0.5 ? 'warning' : 'critical' as const
      },
      {
        name: '用户满意度',
        value: `${(qualityReport.likeRate * 100).toFixed(1)}%`,
        status: qualityReport.likeRate >= 0.8 ? 'good' : qualityReport.likeRate >= 0.6 ? 'warning' : 'critical' as const
      },
      {
        name: '缓存命中率',
        value: `${(stats.cacheHitRate * 100).toFixed(1)}%`,
        status: stats.cacheHitRate >= 0.3 ? 'good' : stats.cacheHitRate >= 0.1 ? 'warning' : 'critical' as const
      }
    ]

    const aiDistribution = stats.aiDistribution
    const topAI = Object.entries(aiDistribution)
      .map(([aiId, interactions]) => {
        const aiReport = this.getAIQualityReport(aiId)
        return {
          aiId,
          interactions,
          avgQuality: (aiReport.averageRelevance + aiReport.averageHelpfulness) / 2
        }
      })
      .sort((a, b) => b.interactions - a.interactions)
      .slice(0, 5)

    return {
      summary: this.generateSummary(stats, qualityReport),
      keyMetrics,
      trends: trendData.slice(-12),
      topAI,
      commonQuestions: qualityReport.topQuestions.slice(0, 5),
      improvementSuggestions: this.generateImprovementSuggestions().slice(0, 5)
    }
  }

  private generateSummary(stats: PerformanceStats, qualityReport: ReturnType<typeof this.getAIQualityReport>): string {
    const parts: string[] = []

    if (stats.totalRequests === 0) {
      return '暂无数据，系统等待中...'
    }

    parts.push(`共处理 ${stats.totalRequests} 次请求`)

    if (stats.errorRate < 0.1) {
      parts.push('，运行稳定')
    } else if (stats.errorRate < 0.2) {
      parts.push('，存在少量错误')
    } else {
      parts.push('，错误率较高')
    }

    if (qualityReport.averageRelevance >= 0.7) {
      parts.push('，回答相关性良好')
    } else {
      parts.push('，回答质量有待提升')
    }

    if (qualityReport.likeRate >= 0.8) {
      parts.push('，用户满意度高')
    }

    return parts.join('')
  }

  exportFullReport(): string {
    const stats = this.getStats()
    const health = this.getSystemHealth()
    const qualityReport = this.getAIQualityReport()
    const insights = this.getPerformanceInsights()
    
    const report = {
      generatedAt: new Date().toISOString(),
      sessionUptime: health.uptime,
      stats,
      health,
      qualityReport,
      insights,
      recentMetrics: this.getMetrics({ limit: 100 }),
      conversationAnalysis: this.getConversationAnalysis(50)
    }

    return JSON.stringify(report, null, 2)
  }
}

export const aiPerformanceMonitor = new AIPerformanceMonitor()
